import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { FileStorageStatus } from 'generated/graphql'
import { useDocumentsLazyQueries, useDocumentsMutations } from 'hooks-queries/documents'
import { useTranslate } from 'lib/tolgee'
import { translations } from 'modules/documents/common/documents.i18n'
import type { IDocument, IUploadProgress } from 'modules/documents/common/documents.types'
import { bytesToMb } from 'modules/documents/common/documents.utils'
import { useZaiChatObserver } from 'modules/zai/common/contexts'
import { documentsService } from 'services'
import { MAX_FILE_SIZE } from 'services/documents/consts'
import { analyticsTracker, datadog } from 'utils'

import {
  DEFAULT_TAKE,
  DELETE_DOCUMENT_ERROR_LOG,
  FETCH_DOCUMENTS_ERROR_LOG,
  FETCH_DOCUMENTS_GENERAL_ERROR_LOG,
  REFRESH_STORAGE_INFO_ERROR_LOG,
  UPLOAD_DOCUMENT_ERROR_LOG,
  UPLOAD_DOCUMENTS_GENERAL_ERROR_LOG,
} from './consts'
import { DocumentsManagementContext } from './context'
import {
  FileUploadStatus,
  type DocumentsManagementContextData,
  type DocumentsManagementProviderProps,
  type IStorageInfo,
  type IUploadQueue,
  type IUploadSession,
} from './types'
import { getDocumentsFormatted, validateFileSizes } from './utils'

export const DocumentsManagementProvider = ({ children }: DocumentsManagementProviderProps) => {
  const { triggerFileCountRefetch } = useZaiChatObserver()
  const { createToast } = useToast()
  const t = useTranslate()

  const [documents, setDocuments] = useState<IDocument[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [storageInfo, setStorageInfo] = useState<IStorageInfo>({ used: 0, limit: 0 })
  const [uploadQueue, setUploadQueue] = useState<IUploadQueue>({
    sessions: [],
    activeSessionIds: [],
  })
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const [isFetchingMore, setIsFetchingMore] = useState(false)

  const documentsTotalListedRef = useRef<number>(0)

  const [getDocumentsList, { fetchMore: fetchMoreDocumentsQuery, refetch: refetchDocumentsQuery, error }] =
    useDocumentsLazyQueries({
      queryName: 'getDocumentsList',
      queryVariables: {
        take: DEFAULT_TAKE,
        status: [FileStorageStatus.Processed, FileStorageStatus.Processing, FileStorageStatus.Requested],
      },
      errorLog: FETCH_DOCUMENTS_ERROR_LOG,
      options: {
        fetchPolicy: 'network-only',
      },
    })

  const [getStorageInfo] = useDocumentsLazyQueries({
    queryName: 'getStorageInfo',
    errorLog: REFRESH_STORAGE_INFO_ERROR_LOG,
    options: { fetchPolicy: 'network-only' },
  })

  const [uploadDocumentAndGetPresignedUrl] = useDocumentsMutations({
    mutationName: 'uploadDocumentAndGetPresignedUrl',
    errorLog: UPLOAD_DOCUMENT_ERROR_LOG,
  })
  const [deleteDocumentMutation] = useDocumentsMutations({
    mutationName: 'deleteDocument',
    errorLog: DELETE_DOCUMENT_ERROR_LOG,
  })

  const isUploading = useMemo(() => uploadQueue.activeSessionIds.length > 0, [uploadQueue.activeSessionIds])

  const uploadProgress = useMemo<IUploadProgress>(() => {
    const activeSessions = uploadQueue.sessions.filter(s => uploadQueue.activeSessionIds.includes(s.id))

    const totalFiles = activeSessions.reduce((sum, s) => sum + s.files.length, 0)
    const completedFiles = activeSessions.reduce(
      (sum, s) => sum + s.files.filter(f => f.status === FileStorageStatus.Processing).length,
      0
    )
    const hasIncompleteFiles = activeSessions.some(s => s.files.some(f => f.status === FileUploadStatus.Uploading))

    return { current: hasIncompleteFiles ? completedFiles + 1 : completedFiles, total: totalFiles }
  }, [uploadQueue.sessions, uploadQueue.activeSessionIds])

  useEffect(() => {
    const completedSessionIds = uploadQueue.sessions
      .filter(s => (s.status === 'completed' || s.status === 'failed') && uploadQueue.activeSessionIds.includes(s.id))
      .map(s => s.id)

    if (completedSessionIds.length === 0) {
      return
    }

    setUploadQueue(prev => ({
      ...prev,
      activeSessionIds: prev.activeSessionIds.filter(id => !completedSessionIds.includes(id)),
    }))
  }, [uploadQueue.activeSessionIds, uploadQueue.sessions])

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploading) {
        e.preventDefault()
        e.returnValue = ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [isUploading])

  const refreshStorageInfo = useCallback(async () => {
    try {
      const response = await getStorageInfo()
      const statistics = response.data?.getWorkspace?.fileStorage?.statistics

      if (statistics?.usedSize && statistics?.limitSize) {
        setStorageInfo({
          used: statistics.usedSize,
          limit: statistics.limitSize,
        })
      }

      triggerFileCountRefetch()
    } catch (error) {
      datadog.dispatchGenericErrorLog({
        log: REFRESH_STORAGE_INFO_ERROR_LOG,
        error,
      })
    }
  }, [getStorageInfo, triggerFileCountRefetch])

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true)

      const response = await getDocumentsList()

      const fileData = response.data?.getWorkspace?.fileStorage?.filesPaginated?.data || []
      const statistics = response.data?.getWorkspace?.fileStorage?.statistics
      const responseCursor = response.data?.getWorkspace?.fileStorage?.filesPaginated?.cursor
      const responseTotalCount = response.data?.getWorkspace?.fileStorage?.filesPaginated?.totalCount || 0

      const transformedDocuments = getDocumentsFormatted({ documents: fileData })

      setDocuments(transformedDocuments)
      setCursor(responseCursor || null)
      setHasMore(!!responseCursor)
      setTotalCount(responseTotalCount)

      documentsTotalListedRef.current = transformedDocuments.length

      if (statistics) {
        setStorageInfo({
          used: statistics.usedSize || 0,
          limit: statistics.limitSize || 0,
        })
      }
    } catch (error) {
      datadog.dispatchGenericErrorLog({
        log: FETCH_DOCUMENTS_GENERAL_ERROR_LOG,
        error,
      })

      documentsTotalListedRef.current = 0
    } finally {
      setIsLoading(false)
    }
  }, [getDocumentsList])

  const fetchMoreDocuments = useCallback(async () => {
    if (!cursor || isFetchingMore || isLoading) return

    try {
      setIsFetchingMore(true)

      const response = await fetchMoreDocumentsQuery({
        variables: {
          take: DEFAULT_TAKE,
          cursor,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          return {
            ...prev,
            getWorkspace: fetchMoreResult.getWorkspace,
          }
        },
      })

      const newCursor = response.data?.getWorkspace?.fileStorage?.filesPaginated?.cursor
      const responseTotalCount = response.data?.getWorkspace?.fileStorage?.filesPaginated?.totalCount || 0

      setCursor(newCursor || null)
      setHasMore(!!newCursor)
      setTotalCount(responseTotalCount)

      const allFiles = response.data?.getWorkspace?.fileStorage?.filesPaginated?.data || []
      const transformedDocuments = getDocumentsFormatted({ documents: allFiles })

      setDocuments(prev => [...prev, ...transformedDocuments])

      documentsTotalListedRef.current = documentsTotalListedRef.current + transformedDocuments.length
    } catch (error) {
      datadog.dispatchGenericErrorLog({
        log: FETCH_DOCUMENTS_GENERAL_ERROR_LOG,
        error,
      })

      documentsTotalListedRef.current = 0
    } finally {
      setIsFetchingMore(false)
    }
  }, [cursor, isFetchingMore, isLoading, fetchMoreDocumentsQuery])

  const refreshDocuments = useCallback(async () => {
    const variables = {
      take: documentsTotalListedRef.current,
    }

    if (!variables.take) return

    const response = await refetchDocumentsQuery(variables)

    const allFiles = response.data?.getWorkspace?.fileStorage?.filesPaginated?.data || []
    const transformedDocuments = getDocumentsFormatted({ documents: allFiles })

    setDocuments(transformedDocuments)
  }, [refetchDocumentsQuery])

  const updateFileProgress = useCallback((sessionId: string, fileId: string, progress: number) => {
    setUploadQueue(prev => ({
      ...prev,
      sessions: prev.sessions.map(session =>
        session.id === sessionId
          ? {
              ...session,
              files: session.files.map(file =>
                file.id === fileId ? { ...file, progress, status: FileUploadStatus.Uploading } : file
              ),
            }
          : session
      ),
    }))
  }, [])

  const updateFileStatus = useCallback((sessionId: string, fileId: string, success: boolean) => {
    setUploadQueue(prev => ({
      ...prev,
      sessions: prev.sessions.map(session =>
        session.id === sessionId
          ? {
              ...session,
              files: session.files.map(file =>
                file.id === fileId
                  ? {
                      ...file,
                      status: success ? FileStorageStatus.Processing : FileUploadStatus.Failed,
                      progress: success ? 100 : file.progress,
                    }
                  : file
              ),
            }
          : session
      ),
    }))
  }, [])

  const completeSession = useCallback((sessionId: string) => {
    setUploadQueue(prev => ({
      ...prev,
      sessions: prev.sessions.map(session =>
        session.id === sessionId ? { ...session, status: 'completed', completedAt: new Date() } : session
      ),
    }))
  }, [])

  const failSession = useCallback((sessionId: string, error: Error) => {
    setUploadQueue(prev => ({
      ...prev,
      sessions: prev.sessions.map(session =>
        session.id === sessionId
          ? {
              ...session,
              status: 'failed',
              completedAt: new Date(),
              files: session.files.map(file => ({
                ...file,
                status:
                  file.status === FileStorageStatus.Processing ? FileStorageStatus.Processing : FileUploadStatus.Failed,
                error,
              })),
            }
          : session
      ),
    }))
  }, [])

  const uploadDocuments = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return

      const { FIL_UPLOAD_SUCCESS, FIL_UPLOAD_ERROR } = analyticsTracker.eventNames

      const { validFiles, oversizedFiles } = validateFileSizes(files)

      if (oversizedFiles.length > 0) {
        const fileSizeLimit = bytesToMb(MAX_FILE_SIZE) + 'MB'

        if (oversizedFiles.length === 1) {
          analyticsTracker.eventTrack(FIL_UPLOAD_ERROR, {
            error: `The file ${oversizedFiles[0]?.name} could not be added because it exceeds the limit of ${fileSizeLimit}.`,
          })

          createToast({
            content: t(translations.errorFileSizeExceededSingle(oversizedFiles[0]?.name)),
          })
        } else {
          analyticsTracker.eventTrack(FIL_UPLOAD_ERROR, {
            error: `We were unable to add ${oversizedFiles.length} files because each one exceeds the ${fileSizeLimit} limit.`,
          })

          createToast({
            content: t(translations.errorFileSizeExceededMultiple(oversizedFiles.length)),
          })
        }
      }

      if (validFiles.length === 0) return

      const currentUploadSize = validFiles.reduce((sum, file) => sum + file.size, 0)

      const pendingUploadSize = uploadQueue.sessions
        .filter(s => s.status === 'uploading')
        .reduce((sum, session) => {
          return sum + session.files.reduce((fileSum, file) => fileSum + file.size, 0)
        }, 0)

      const totalFileSize = currentUploadSize + pendingUploadSize

      if (storageInfo.used + totalFileSize > storageInfo.limit) {
        analyticsTracker.eventTrack(FIL_UPLOAD_ERROR, {
          error: 'Insufficient storage space. Delete some files to continue.',
        })

        createToast({
          content: t(translations.errorStorageQuotaExceeded),
        })
        return
      }

      const sessionId = uuidv4()
      const session: IUploadSession = {
        id: sessionId,
        status: 'uploading',
        files: validFiles.map(file => ({
          id: uuidv4(),
          file,
          fileName: file.name,
          size: file.size,
          status: FileUploadStatus.Uploading,
          progress: 0,
        })),
        createdAt: new Date(),
        startedAt: new Date(),
      }

      try {
        setUploadQueue(prev => ({
          ...prev,
          activeSessionIds: [...prev.activeSessionIds, sessionId],
          sessions: [session, ...prev.sessions],
        }))

        const optimisticDocs: IDocument[] = session.files.map(f => ({
          id: f.id,
          fileName: f.fileName,
          size: f.size,
          createdAt: new Date(),
          status: FileUploadStatus.Uploading,
        }))

        setDocuments(prev => [...optimisticDocs, ...prev])
        setTotalCount(prev => prev + optimisticDocs.length)

        const response = await uploadDocumentAndGetPresignedUrl({
          variables: {
            files: session.files.map(f => ({
              id: f.id,
              fileName: f.fileName,
              size: f.size,
            })),
          },
        })

        const uploadConfigs = response.data?.createFileStorageUploadUrls?.uploads

        if (!uploadConfigs || uploadConfigs.length === 0) {
          throw new Error('No upload configurations received from API')
        }

        let successfulUploadsCount = 0

        await documentsService.batchUploadToS3({
          files: uploadConfigs.map((config, index) => ({
            file: session.files[index].file,
            uploadConfig: {
              id: config.id,
              uploadUrl: config.uploadUrl,
              fields: config.fields,
            },
          })),
          onProgress: (fileId, progress) => {
            updateFileProgress(sessionId, fileId, progress)
          },
          onFileComplete: result => {
            updateFileStatus(sessionId, result.fileId, result.success)

            setDocuments(prev => {
              // Since we don't have a retry mechanism for individual documents, remove failed documents from the list
              return prev.reduce<IDocument[]>((acc, doc) => {
                if (doc.id === result.fileId) {
                  if (result.success) {
                    acc.push({ ...doc, status: FileStorageStatus.Processing })
                  }
                } else {
                  acc.push(doc)
                }
                return acc
              }, [])
            })

            if (result.success) {
              successfulUploadsCount++
            } else {
              const fileName = session.files.find(f => f.id === result.fileId)?.fileName || ''

              analyticsTracker.eventTrack(FIL_UPLOAD_ERROR, {
                error: `Failed to send the "${fileName}" file. Please try again.`,
              })

              createToast({
                content: t(translations.errorUploadFailed(fileName)),
              })
            }
          },
        })

        completeSession(sessionId)

        if (successfulUploadsCount > 0) {
          analyticsTracker.eventTrack(FIL_UPLOAD_SUCCESS, {
            message: successfulUploadsCount === 1 ? `Document sent.` : `${successfulUploadsCount} documents sent.`,
          })

          createToast({
            content: t(translations.uploadSuccessMultiple(successfulUploadsCount)),
          })

          documentsTotalListedRef.current = documentsTotalListedRef.current + successfulUploadsCount

          refreshDocuments()

          refreshStorageInfo()
        }
      } catch (error) {
        const uploadError = error instanceof Error ? error : new Error('Unknown upload error')
        failSession(sessionId, uploadError)

        analyticsTracker.eventTrack(FIL_UPLOAD_ERROR, {
          error: uploadError.message,
        })

        datadog.dispatchGenericErrorLog({
          log: UPLOAD_DOCUMENTS_GENERAL_ERROR_LOG,
          error: uploadError,
        })
      }
    },
    [
      completeSession,
      createToast,
      failSession,
      refreshStorageInfo,
      storageInfo.limit,
      storageInfo.used,
      t,
      updateFileProgress,
      updateFileStatus,
      uploadDocumentAndGetPresignedUrl,
      uploadQueue.sessions,
      refreshDocuments,
    ]
  )

  const deleteDocument = useCallback(
    async (documentId: string) => {
      try {
        setIsLoading(true)

        const response = await deleteDocumentMutation({
          variables: { fileId: documentId },
        })

        if (response.data?.deleteFileStorageFile?.success) {
          setDocuments(prev => prev.filter(doc => doc.id !== documentId))
          setTotalCount(prev => Math.max(0, prev - 1))

          createToast({
            content: t(translations.documentDeletedSuccessfully),
          })

          documentsTotalListedRef.current = documentsTotalListedRef.current - 1

          refreshStorageInfo()
        }
      } catch (error) {
        createToast({
          content: t(translations.errorDeletingDocument),
        })
        datadog.dispatchGenericErrorLog({
          log: DELETE_DOCUMENT_ERROR_LOG,
          error,
        })
      } finally {
        setIsLoading(false)
      }
    },
    [createToast, deleteDocumentMutation, refreshStorageInfo, t]
  )

  const value: DocumentsManagementContextData = useMemo(
    () => ({
      documents,
      isLoading,
      isUploading,
      uploadProgress,
      error: !!error,
      storageInfo,
      cursor,
      hasMore,
      isFetchingMore,
      totalCount,
      fetchDocuments,
      uploadDocuments,
      deleteDocument,
      refreshStorageInfo,
      fetchMoreDocuments,
      refreshDocuments,
    }),
    [
      documents,
      isLoading,
      isUploading,
      uploadProgress,
      error,
      storageInfo,
      cursor,
      hasMore,
      isFetchingMore,
      totalCount,
      fetchDocuments,
      uploadDocuments,
      deleteDocument,
      refreshStorageInfo,
      fetchMoreDocuments,
      refreshDocuments,
    ]
  )

  return <DocumentsManagementContext.Provider value={value}>{children}</DocumentsManagementContext.Provider>
}
